import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";
import { useLeague } from "../context/LeagueContext";
import { SEASON_YEAR, TOTAL_LINEUP_WEEKS } from "../utils/season";
import { normalizeTeamName } from "../utils/teamName";
import { getCached, setCached, ttlForGames, LIVE_TTL_MS } from "../utils/queryCache";

// Backs the League tab's Hall of Fame / Hall of Shame page and the one-line
// teaser above the standings, so neither can drift from what the full list
// says the league record actually is.
//
// weekly_lineups has no season/year column, so every ranking here is the
// league's entire history, not one season of it. That is correct as long as
// one league is one season (a new season means a new league, not a re-draft
// into this one) — if leagues ever persist across seasons, this needs a year
// filter it doesn't have yet.
// 3, not 10: a top 10 for a league that might only have 8 managers read as
// padding rather than a ranking, and it buried the interesting end of the
// list (barely-worst, barely-best) under entries nobody was looking for.
const RECORDS_LIMIT = 3;
// Best Week and Worst Week can open out to this many. They rank a pool of
// managers times weeks, dozens of entries by October, so ten is a real
// ranking there where ten of eight managers would not be. It costs nothing:
// every week is already in hand to find the top three.
const WEEK_RECORDS_LIMIT = 10;

// How long the record book is held on the device. It used to be a day, which
// suited a page that only read finished weeks, but it reads the current week
// too: a freeze, or a Saturday's results, could take until the next day to
// appear. Shorter while games are running, and still well above the live pages'
// 2 minutes, since this is the largest request in the app (every lineup in the
// league for the season, plus every team's points and the schedule). A
// manager's own lineup save clears it at once through invalidateLeague.
const RECORDS_LIVE_TTL_MS = 30 * 60 * 1000;
const RECORDS_IDLE_TTL_MS = 2 * 60 * 60 * 1000;

// Best-N and worst-N from one sorted pool. "Worst" is ascending (most
// negative/lowest first), matching the convention already used for NCAA
// team highs before that section was cut from the Scoreboard tab.
function topAndBottom(entries, limit = RECORDS_LIMIT) {
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  return {
    top: sorted.slice(0, limit),
    bottom: [...sorted].reverse().slice(0, limit),
  };
}

export function useLeagueRecords() {
  const { leagueId, members, currentWeek } = useLeague();
  const [lineupRows, setLineupRows] = useState([]);
  const [pointsBySlug, setPointsBySlug] = useState({});
  // Which teams actually had a game each week, for telling "on a bye" (a
  // decided zero) apart from "hasn't kicked off yet" (undecided) when
  // judging a perfect week. Keyed by week, one Set of slugs per week.
  const [teamsPlayingByWeek, setTeamsPlayingByWeek] = useState({});
  // Weeks that still have a game to finish, for telling when the season's
  // last week is over. See isFinishedWeek.
  const [openGameWeeks, setOpenGameWeeks] = useState(() => new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    // See RECORDS_LIVE_TTL_MS. Persisted, so a relaunch mid-season doesn't
    // start from an empty page.
    //
    // Versioned: a copy persisted before the query gained frozen_teams and
    // team_points would otherwise be read back for up to a day and score every
    // frozen captain on its final result.
    const cacheKey = `hallOfFame:v2:${leagueId}`;

    const applyRows = ({ lineups, statRows, games }) => {
      const points = {};
      (statRows || []).forEach((r) => { points[r.team_id] = r.weekly_points || {}; });

      const playingByWeek = {};
      (games || []).forEach((g) => {
        if (g.home_team) (playingByWeek[g.week] = playingByWeek[g.week] || new Set()).add(normalizeTeamName(g.home_team));
        if (g.away_team) (playingByWeek[g.week] = playingByWeek[g.week] || new Set()).add(normalizeTeamName(g.away_team));
      });

      // A postponed game is not coming back that week, so it does not hold
      // the week open.
      const openWeeks = new Set();
      (games || []).forEach((g) => {
        if (g.game_status !== "final" && g.game_status !== "postponed") openWeeks.add(Number(g.week));
      });

      setLineupRows(lineups || []);
      setPointsBySlug(points);
      setTeamsPlayingByWeek(playingByWeek);
      setOpenGameWeeks(openWeeks);
    };

    (async () => {
      setLoading(true);

      const cached = getCached(cacheKey);
      if (cached) {
        applyRows(cached);
        setLoading(false);
        return;
      }

      const [{ data: lineups }, { data: statRows }, { data: games }] = await Promise.all([
        supabase
          .from("weekly_lineups")
          .select("member_id, week, starters, bench, captain, trip_play_team, frozen_teams, team_points, points")
          .eq("league_id", leagueId),
        // Read fresh rather than the boot-time teams map, same reasoning as
        // ScoreboardTab: espn.js scores a game while it runs, so a cached map
        // goes stale inside a quarter. Cached here at the record-book level
        // instead, once a day, not per read.
        supabase
          .from("team_season_stats")
          .select("team_id, weekly_points")
          .eq("season_year", SEASON_YEAR),
        supabase
          .from("games")
          .select("home_team, away_team, week, game_status, period")
          .eq("year", SEASON_YEAR),
      ]);
      if (cancelled) return;

      applyRows({ lineups, statRows, games });
      // ttlForGames answers "is anything live" with the same rule the live
      // pages use.
      const ttl = ttlForGames(games) === LIVE_TTL_MS ? RECORDS_LIVE_TTL_MS : RECORDS_IDLE_TTL_MS;
      setCached(cacheKey, { lineups, statRows, games }, ttl, { persist: true });
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [leagueId]);

  const memberById = new Map((members || []).map((m) => [m.id, m]));
  const nameFor = (memberId) => {
    const m = memberById.get(memberId);
    return {
      teamName: m?.team_name || "Unnamed Team",
      firstName: m?.first_name || "",
      avatar_url: m?.avatar_url || null,
    };
  };
  const teamWeekPoints = (slug, week) => {
    const pts = pointsBySlug[slug]?.[String(week)];
    return typeof pts === "number" ? pts : null;
  };
  // Same as teamWeekPoints, except a bye resolves to a decided 0 instead of
  // null. Only used for the perfect-week check below: a bye is over and
  // worth nothing, but an unplayed team could still turn into anything, and
  // the two must not be treated alike when judging whether a bench beat a
  // starter.
  const resolvedTeamPoints = (slug, week) => {
    const raw = teamWeekPoints(slug, week);
    if (raw != null) return raw;
    const playing = teamsPlayingByWeek[week];
    return playing && !playing.has(slug) ? 0 : null;
  };

  // points defaults to 0 until scoring runs, so 0 is excluded as "not played
  // yet" rather than a genuine zero week, the same convention every other
  // manager-week ranking in this app already uses.
  // The record book counts finished weeks only, every record in it.
  //
  // A week still being played is a small number on its way to a bigger one.
  // On a Saturday afternoon it would be nearly everyone's worst week, a
  // captain two quarters into a game would be moving the season's captain
  // totals, and each visit would show a slightly different Hall. So the week
  // the league is on is left out, and joins when the week advances (10am
  // Sunday Eastern, backend/season.js).
  //
  // The season's last week has nothing after it to advance to, so it would
  // never join. It closes when its own games have all finished instead.
  // With no current week to go by, every week counts.
  const liveWeek = Number(currentWeek);
  const isFinishedWeek = (week) => {
    const n = Number(week);
    if (!(liveWeek >= 1) || n < liveWeek) return true;
    if (n === liveWeek && liveWeek >= TOTAL_LINEUP_WEEKS) {
      return Boolean(teamsPlayingByWeek[n]) && !openGameWeeks.has(n);
    }
    return false;
  };
  const finishedRows = lineupRows.filter((r) => isFinishedWeek(r.week));

  const weekEntries = finishedRows
    .filter((r) => typeof r.points === "number" && r.points !== 0)
    .map((r) => ({ memberId: r.member_id, week: r.week, value: r.points, ...nameFor(r.member_id) }));
  const week = topAndBottom(weekEntries, WEEK_RECORDS_LIMIT);

  // Each manager's own best and worst week, for the standings columns.
  const weekRangeByMember = new Map();
  weekEntries
    .forEach((e) => {
      const r = weekRangeByMember.get(e.memberId) || { memberId: e.memberId, best: null, worst: null };
      if (!r.best || e.value > r.best.value) r.best = { value: e.value, week: e.week };
      if (!r.worst || e.value < r.worst.value) r.worst = { value: e.value, week: e.week };
      weekRangeByMember.set(e.memberId, r);
    });

  // Points a manager left on their own bench, summed across the whole
  // season rather than one week: a manager who repeatedly stacks a bench
  // that outscores their lineup earns this record over the season, not in
  // any single week.
  //
  // Only what a benched team scored above zero counts, team by team. A team
  // benched in a week it lost points was the right call: starting it could
  // not have added anything, so there was nothing left on the bench. Summing
  // the raw points let those weeks cancel out real ones, and could take the
  // season total below zero.
  const benchTotals = new Map();
  finishedRows.forEach((r) => {
    const total = (r.bench || []).reduce((sum, slug) => sum + Math.max(0, teamWeekPoints(slug, r.week) ?? 0), 0);
    benchTotals.set(r.member_id, (benchTotals.get(r.member_id) || 0) + total);
  });
  const benchWorst = [...benchTotals.entries()]
    .filter(([, total]) => total > 0)
    .map(([memberId, total]) => ({ memberId, value: total, ...nameFor(memberId) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, RECORDS_LIMIT);

  // What the captain actually scored with the armband's multiplier applied,
  // every week rather than only the currently-viewed one. teamSlug travels
  // with each entry so the page can show which team was captained, not just
  // the manager and the number.
  //
  // A frozen captain counts the score it was frozen at, which the scorer keeps
  // in the lineup's team_points with the multiplier applied. The team's own
  // weekly points carry on to the final whistle, and reading those instead
  // would judge a freeze on the result it was used to avoid.
  const captainEntries = finishedRows
    .map((r) => {
      if (!r.captain) return null;
      const isTripPlay = r.trip_play_team === r.captain;
      const multiplier = isTripPlay ? 5 : 2;
      const frozen = (r.frozen_teams || []).includes(r.captain)
        ? r.team_points?.[r.captain]
        : undefined;
      const base = typeof frozen === "number" ? frozen / multiplier : teamWeekPoints(r.captain, r.week);
      if (base == null) return null;
      return {
        memberId: r.member_id,
        week: r.week,
        teamSlug: r.captain,
        base,
        isTripPlay,
        value: typeof frozen === "number" ? frozen : base * multiplier,
        ...nameFor(r.member_id),
      };
    })
    .filter(Boolean);

  // Captain Whisperer: each manager's captain points summed over the season,
  // top three and bottom three. It replaced a single best and worst captain
  // week, which ranked one number with only about twenty possible values, so
  // it filled with identical ties within weeks, and which let the 5x Play's
  // week in at five times the scale of every other entry.
  //
  // 5x Play weeks are left out entirely rather than counted at 2x, since that
  // week has its own record below and the armband decision in it is really the
  // 5x decision. Ties sort by team name so the order is at least stable.
  const captainSeasonTotals = new Map();
  captainEntries
    .filter((e) => !e.isTripPlay)
    .forEach((e) => {
      const t = captainSeasonTotals.get(e.memberId) || { value: 0, weeks: 0 };
      captainSeasonTotals.set(e.memberId, { value: t.value + e.value, weeks: t.weeks + 1 });
    });
  const captainSeason = [...captainSeasonTotals.entries()]
    .map(([memberId, t]) => ({ memberId, value: t.value, weeks: t.weeks, ...nameFor(memberId) }));
  const byTotal = (dir) => (a, b) => dir * (b.value - a.value) || a.teamName.localeCompare(b.teamName);
  const captain = {
    top: [...captainSeason].sort(byTotal(1)).slice(0, RECORDS_LIMIT),
    bottom: [...captainSeason].sort(byTotal(-1)).slice(0, RECORDS_LIMIT),
  };

  // The 5x Play: one per season per manager (migration 032), so this is
  // "find that one week and rank it," not a fresh scan.
  const tripPlayEntries = captainEntries.filter((e) => e.isTripPlay);
  const tripPlay = topAndBottom(tripPlayEntries);

  // Freezes, judged by what they turned out to be worth: the frozen score
  // against what the team finished on without it, multiplier applied both
  // times. The same sum the panel under a frozen row shows, so the two agree.
  //
  // Free to work out: every lineup's frozen teams and frozen scores, and
  // every team's weekly points, are already in hand. Finished weeks only, like
  // everything here, so a freeze is judged on how its game ended. Best is the
  // freezes that saved points, worst the ones that cost; one that made no
  // difference is in neither.
  const freezeEntries = [];
  finishedRows.forEach((r) => {
    (r.frozen_teams || []).forEach((slug) => {
      const frozen = r.team_points?.[slug];
      const base = teamWeekPoints(slug, r.week);
      if (typeof frozen !== "number" || base == null) return;
      const factor = r.captain === slug ? (r.trip_play_team === slug ? 5 : 2) : (r.trip_play_team === slug ? 3 : 1);
      freezeEntries.push({
        memberId: r.member_id,
        week: r.week,
        teamSlug: slug,
        value: frozen - base * factor,
        ...nameFor(r.member_id),
      });
    });
  });
  const freeze = {
    top: freezeEntries.filter((e) => e.value > 0).sort((a, b) => b.value - a.value).slice(0, RECORDS_LIMIT),
    bottom: freezeEntries.filter((e) => e.value < 0).sort((a, b) => a.value - b.value).slice(0, RECORDS_LIMIT),
  };

  // Perfect week: no benched team outscored any started team, and the
  // captain was the highest (or tied-highest) scorer on the roster. Not
  // hardcoded to "the two worst on the bench": bench depth varies by league
  // (whatever the draft didn't spend on starters), so the general form is
  // "the whole bench sits at or below the whole starting five." A week whose
  // starters aren't all decided yet (mid-week, or the data simply doesn't
  // exist for it) is skipped rather than guessed at.
  //
  // Every instance, not a per-manager leaderboard: a perfect week is rare
  // enough that a league is unlikely to ever need a "most" cut, and listing
  // each one is more in keeping with "hall of fame" than ranking who has the
  // most trophies. Newest first, since a recent one is the interesting one.
  const perfectWeek = [];
  finishedRows.forEach((r) => {
    const starters = (r.starters || []).filter(Boolean);
    const bench = (r.bench || []).filter(Boolean);
    if (!r.captain || starters.length === 0) return;
    const starterPts = starters.map((s) => resolvedTeamPoints(s, r.week));
    if (starterPts.some((p) => p == null)) return;
    const benchPts = bench.map((s) => resolvedTeamPoints(s, r.week)).filter((p) => p != null);
    const worstStarter = Math.min(...starterPts);
    if (benchPts.length > 0 && Math.max(...benchPts) > worstStarter) return;
    if (resolvedTeamPoints(r.captain, r.week) !== Math.max(...starterPts)) return;
    perfectWeek.push({ memberId: r.member_id, week: r.week, ...nameFor(r.member_id) });
  });
  perfectWeek.sort((a, b) => b.week - a.week || a.teamName.localeCompare(b.teamName));

  // Decision accuracy: of every start-or-bench call and every captain call a
  // manager has made, how many were the correct one, as a running percentage
  // across the whole season. Every rostered team is its own decision, not
  // just the bench slots: a starter who should have been benched for a
  // higher-scoring bench team is exactly as wrong as that bench team being
  // left out, so a 5-starter, 2-bench week is 7 selection decisions, not 2,
  // plus 1 for captain.
  //
  // A team's placement is judged against the true cutoff: sort every
  // rostered team by raw score and find the value at the Sth spot (S = how
  // many actually started). Anyone above that value belongs in the starting
  // five; anyone below belongs on the bench; anyone started or benched on
  // the wrong side of it is one wrong decision. Comparing each side only to
  // the single best bench score or single worst starter score (an earlier,
  // wrong version of this) over-penalizes: it can flag a starter as wrong
  // just because one bench team scored higher, even when a different,
  // clearly worse starter is the one that should have been swapped out, not
  // this one.
  //
  // A genuine tie sitting exactly on that cutoff line (two or more teams
  // sharing the boundary score, splitting a smaller number of remaining
  // slots) has no correct answer to compare against, so none of the tied
  // teams are marked wrong regardless of which side they actually landed on.
  //
  // The captain is correct if they were the highest (or tied-highest) raw
  // scorer among the starters.
  //
  // A week only contributes once every rostered team (starters and bench
  // alike) has a decided score — unlike the perfect-week check above, which
  // only needed starters decided to rule a week imperfect, this is a tally
  // rather than a gate, so a bench team still to kick off must not be
  // silently counted as either right or wrong.
  const decisionTotals = new Map();
  const decisionCorrect = new Map();
  // Captain accuracy is the same underlying call, tallied on its own: one
  // captain decision per decided week, separate from the selection tally
  // above, so a manager who reads the bench well but picks the wrong captain
  // (or the reverse) shows up differently on each stat rather than one
  // blended number hiding which skill they actually have.
  const captainTotals = new Map();
  const captainCorrect = new Map();
  finishedRows.forEach((r) => {
    const starters = (r.starters || []).filter(Boolean);
    const bench = (r.bench || []).filter(Boolean);
    if (!r.captain || starters.length === 0) return;
    const starterPts = starters.map((s) => resolvedTeamPoints(s, r.week));
    if (starterPts.some((p) => p == null)) return;
    const benchPts = bench.map((s) => resolvedTeamPoints(s, r.week));
    if (benchPts.some((p) => p == null)) return;

    const roster = [
      ...starterPts.map((points) => ({ points, started: true })),
      ...benchPts.map((points) => ({ points, started: false })),
    ];
    const sortedDesc = roster.map((x) => x.points).sort((a, b) => b - a);
    const cutoff = sortedDesc[starters.length - 1];
    const tiedAtCutoff = roster.filter((x) => x.points === cutoff).length > 1;

    const correctSelections = roster.filter((x) => {
      if (x.points === cutoff && tiedAtCutoff) return true;
      return x.points >= cutoff ? x.started : !x.started;
    }).length;

    const bestStarter = Math.max(...starterPts);
    const correctCaptain = resolvedTeamPoints(r.captain, r.week) === bestStarter ? 1 : 0;

    decisionTotals.set(r.member_id, (decisionTotals.get(r.member_id) || 0) + roster.length + 1);
    decisionCorrect.set(r.member_id, (decisionCorrect.get(r.member_id) || 0) + correctSelections + correctCaptain);
    captainTotals.set(r.member_id, (captainTotals.get(r.member_id) || 0) + 1);
    captainCorrect.set(r.member_id, (captainCorrect.get(r.member_id) || 0) + correctCaptain);
  });
  // Only managers with at least one fully-decided week: a 0-for-0 record
  // isn't a 0%, it's no data, and ranking it as the worst on the board would
  // be judging a manager on weeks that have not happened yet.
  const decisionAccuracy = (members || [])
    .map((m) => {
      const total = decisionTotals.get(m.id) || 0;
      const correct = decisionCorrect.get(m.id) || 0;
      return { memberId: m.id, total, correct, value: total > 0 ? Math.round((correct / total) * 1000) / 10 : null, ...nameFor(m.id) };
    })
    .filter((e) => e.total > 0)
    .sort((a, b) => b.value - a.value);

  const captainAccuracy = (members || [])
    .map((m) => {
      const total = captainTotals.get(m.id) || 0;
      const correct = captainCorrect.get(m.id) || 0;
      return { memberId: m.id, total, correct, value: total > 0 ? Math.round((correct / total) * 1000) / 10 : null, ...nameFor(m.id) };
    })
    .filter((e) => e.total > 0)
    .sort((a, b) => b.value - a.value);

  return {
    loading,
    // week is the three always on show; weekMore is 4th to 10th, for the
    // list's "show more".
    fame: { week: week.top.slice(0, RECORDS_LIMIT), weekMore: week.top.slice(RECORDS_LIMIT), captain: captain.top, tripPlay: tripPlay.top, freeze: freeze.top, perfectWeek },
    shame: { week: week.bottom.slice(0, RECORDS_LIMIT), weekMore: week.bottom.slice(RECORDS_LIMIT), bench: benchWorst, captain: captain.bottom, tripPlay: tripPlay.bottom, freeze: freeze.bottom },
    decisionAccuracy,
    captainAccuracy,
    // The same season totals the two records above are cut from, for every
    // manager rather than the top three, so the standings columns and the
    // Hall can never quote different numbers for one manager. A manager with
    // no captained week yet is absent from captainPoints, not a zero.
    benchPoints: [...benchTotals.entries()].map(([memberId, value]) => ({ memberId, value })),
    captainPoints: captainSeason.map(({ memberId, value, weeks }) => ({ memberId, value, weeks })),
    weekRange: [...weekRangeByMember.values()],
  };
}
